import React, { useState, useEffect, useRef } from "react";
import { saveMedicalAssets, generateBaselineNumber, formatBaselineNumber, shortBaselineLabel, labReportUrl } from "../data/ccbaseline";
import "../styles/baselineview.css";

// ── Icons ─────────────────────────────────────────────────────
import iconCondition from "../assets/baseline/Condidtion.svg";
import iconHba1c     from "../assets/baseline/HbA1c.svg";
import iconDoctor    from "../assets/baseline/Doctor.svg";
import iconDuration  from "../assets/baseline/Duration.svg";
import iconMedication from "../assets/baseline/medication.svg";
import iconActivity  from "../assets/baseline/activity.svg";
import iconSleepGoal from "../assets/baseline/Sleep Goal.svg";
import iconSleepQual from "../assets/baseline/sleepqual.svg";
import iconDiet      from "../assets/baseline/diet.svg";
import iconGoal      from "../assets/baseline/Goal.svg";
import iconCalories  from "../assets/baseline/Calories.svg";
import iconProtein   from "../assets/baseline/Protien.svg";
import iconCarbs     from "../assets/baseline/Carbs.svg";
import iconFat       from "../assets/baseline/Fat.svg";
import iconBmi       from "../assets/baseline/BMI.svg";
import iconLike      from "../assets/baseline/lIKE.svg";
import iconDislike   from "../assets/baseline/Dislike.svg";
import iconTiming    from "../assets/baseline/timing.svg";

import iconECG from "../assets/baseline/ECG.svg";
import iconHeartSound from "../assets/baseline/heartsound.svg";
import iconLungSound from "../assets/baseline/lungsound.svg";
import iconBMI from "../assets/baseline/BMI.svg";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;


function openInTab(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const SLOTS = [
  {
    key: "ecg_link",
    reportKey: "ecg_link",
    icon: iconECG,
    label: "ECG",
    hint: "Paste ECG report URL",
    accent: "#ef4444",
  },
  {
    key: "heart_sound_link",
    reportKey: "heart_sound_link",
    icon: iconHeartSound,
    label: "Heart Sound",
    hint: "Paste heart sound recording URL",
    accent: "#f97316",
  },
  {
    key: "lung_sound_link",
    reportKey: "lung_sound_link",
    icon: iconLungSound,
    label: "Lung Sound",
    hint: "Paste lung sound recording URL",
    accent: "#3b82f6",
  },
  {
    key: "bmi_link",
    reportKey: "bmi_link",
    icon: iconBMI,
    label: "BMI Report",
    hint: "Paste BMI / body analysis URL",
    accent: "#10b981",
  },
];

// ── NCD Panel ─────────────────────────────────────────────────
function NcdPanel({ ncd, profile }) {
  const risks = ncd?.risks
    ? Object.entries(ncd.risks).filter(([, v]) => v).map(([k]) => k)
    : [];

  const clinical = [
    { label: "Condition",    val: ncd?.condition,                                             icon: iconCondition },
    { label: "HbA1c",        val: ncd?.hba1c != null ? `${ncd.hba1c}%` : null,                 icon: iconHba1c },
    { label: "Doctor",       val: ncd?.doctor_name,                                            icon: iconDoctor },
    { label: "Duration",     val: ncd?.diab_duration,                                          icon: iconDuration },
    { label: "Medications",  val: ncd?.medications,                                            icon: iconMedication },
    { label: "Activity",     val: ncd?.lifestyle?.activity,                                    icon: iconActivity },
    { label: "Sleep",        val: ncd?.lifestyle?.sleep_duration,                               icon: iconSleepGoal },
    { label: "Sleep Qual.",  val: ncd?.lifestyle?.sleep_quality,                                icon: iconSleepQual },
    { label: "Diet",         val: ncd?.lifestyle?.eating_pattern,                               icon: iconDiet },
  ].filter((r) => r.val);

  const targets = [
    { label: "Goal",      val: ncd?.targets?.goal,                                                          icon: iconGoal },
    { label: "Calories",  val: ncd?.targets?.target_calories ? `${ncd.targets.target_calories} kcal` : null, icon: iconCalories },
    { label: "Protein",   val: ncd?.targets?.protein_g ? `${ncd.targets.protein_g}g` : null,                 icon: iconProtein },
    { label: "Carbs",     val: ncd?.targets?.carbs_g ? `${ncd.targets.carbs_g}g` : null,                     icon: iconCarbs },
    { label: "Fat",       val: ncd?.targets?.fat_g ? `${ncd.targets.fat_g}g` : null,                         icon: iconFat },
    { label: "BMI",       val: ncd?.targets?.bmi ? `${ncd.targets.bmi} · ${ncd.targets.bmi_category}` : null, icon: iconBmi },
  ].filter((r) => r.val);

  const food = [
    { label: "Likes",    val: ncd?.food_preferences?.likes,       icon: iconLike },
    { label: "Dislikes", val: ncd?.food_preferences?.dislikes,    icon: iconDislike },
    { label: "Timing",   val: ncd?.food_preferences?.meal_timing, icon: iconTiming },
  ].filter((r) => r.val);

  return (
    <div className="bv-ncd">
      {/* Clinical */}
      {clinical.length > 0 && (
        <>
          <div className="bv-ncd-section">Clinical</div>
          <div className="bv-ncd-grid">
            {clinical.map((r) => (
              <div key={r.label} className="bv-ncd-cell">
                <span className="bv-ncd-icon-box">
                  <img src={r.icon} alt="" className="bv-ncd-icon" />
                </span>
                <span className="bv-ncd-text">
                  <span className="bv-ncd-cl">{r.label}</span>
                  <span className="bv-ncd-cv">{r.val}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Targets */}
      {targets.length > 0 && (
        <>
          <div className="bv-ncd-section">Targets</div>
          <div className="bv-ncd-grid">
            {targets.map((r) => (
              <div key={r.label} className="bv-ncd-cell accent">
                <span className="bv-ncd-icon-box">
                  <img src={r.icon} alt="" className="bv-ncd-icon" />
                </span>
                <span className="bv-ncd-text">
                  <span className="bv-ncd-cl">{r.label}</span>
                  <span className="bv-ncd-cv">{r.val}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Food */}
      {food.length > 0 && (
        <>
          <div className="bv-ncd-section">Food Preferences</div>
          <div className="bv-ncd-grid">
            {food.map((r) => (
              <div key={r.label} className="bv-ncd-cell">
                <span className="bv-ncd-icon-box">
                  <img src={r.icon} alt="" className="bv-ncd-icon" />
                </span>
                <span className="bv-ncd-text">
                  <span className="bv-ncd-cl">{r.label}</span>
                  <span className="bv-ncd-cv">{r.val}</span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ── Asset row ─────────────────────────────────────────────────
function AssetRow({ icon, label, url, accent }) {
  const has = !!url;
  return (
    <div className={`bv-arow ${has ? "filled" : "empty"}`} style={{ "--ac": accent }}>
      <div className="bv-arow-icon-wrap">
        <img src={icon} alt={label} className="bv-arow-icon" />
      </div>
      <div className="bv-arow-body">
        <span className="bv-arow-label">{label}</span>
        {has
          ? <span className="bv-arow-url">{url}</span>
          : <span className="bv-arow-nil">Not uploaded</span>
        }
      </div>
      {has && (
        <button className="bv-arow-open" onClick={() => openInTab(url)}>
          Open
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M1.5 9.5L9.5 1.5M9.5 1.5H4M9.5 1.5V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}

// ── Baseline strip ────────────────────────────────────────────
function BaselineStrip({ baselines, activeIdx, onSelect, onNew }) {
  return (
    <div className="bv-strip">
      {baselines.map((bl, i) => (
        <button
          key={bl.baseline_number ?? i}
          className={`bv-strip-pill ${i === activeIdx ? "active" : ""}`}
          onClick={() => onSelect(i)}
        >
          {shortBaselineLabel(bl.baseline_number)}
        </button>
      ))}
      <button className="bv-strip-new" onClick={onNew}>
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
          <path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        New
      </button>
    </div>
  );
}

// ── Link field ────────────────────────────────────────────────
function LinkField({ icon, label, hint, fieldKey, value, onChange, accent }) {
  return (
    <div className="bv-field" style={{ "--ac": accent }}>
      <label className="bv-field-label">
        <img
          src={icon}
          alt={label}
          className="bv-field-icon"
        />
        {label}
      </label>

      <input
        className="bv-field-input"
        type="url"
        placeholder={hint}
        value={value || ""}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        spellCheck={false}
        autoComplete="off"
      />
    </div>
  );
}
// ── PDF field ─────────────────────────────────────────────────
function LabField({ file, onChange }) {
  const ref = useRef();
  return (
    <div className="bv-field" style={{ "--ac": "#8b5cf6" }}>
      <label className="bv-field-label">
        <img
          src={iconECG}
          alt="Lab Report"
          className="bv-field-icon"
        />
        Lab Report <em style={{ fontStyle: "normal", opacity: 0.55, fontSize: "11px", marginLeft: 4 }}>PDF</em>
      </label>
      <div className="bv-file-trigger" onClick={() => ref.current?.click()}>
        <span className="bv-file-name">
          {file ? file.name : <span className="bv-file-ph">Click to choose a PDF file…</span>}
        </span>
        <span className="bv-file-badge">Browse</span>
        {file && (
          <button className="bv-file-clear" type="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}>✕</button>
        )}
        <input ref={ref} type="file" accept="application/pdf" style={{ display: "none" }}
          onChange={(e) => { onChange(e.target.files[0] || null); e.target.value = ""; }} />
      </div>
    </div>
  );
}

// ── 12-hour Date & Time field (AM/PM) ───────────────────────────
function DateTimeField({ dateVal, hour12, minuteV, ampm, onDate, onHour, onMinute, onAmpm }) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className="bv-field" style={{ "--ac": "#6366f1", marginTop: 10 }}>
      <label className="bv-field-label">
        Date &amp; Time{" "}
        <em style={{ fontStyle: "normal", opacity: 0.5, fontSize: "11px", marginLeft: 4 }}>
          optional
        </em>
      </label>
      <div className="bv-dt-pill">
        <input
          className="bv-dt-date"
          type="date"
          value={dateVal}
          onChange={(e) => onDate(e.target.value)}
        />
        <span className="bv-dt-sep" />
        <select className="bv-dt-sel" value={hour12} onChange={(e) => onHour(e.target.value)}>
          {hours.map((h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
          ))}
        </select>
        <span className="bv-dt-colon">:</span>
        <select className="bv-dt-sel" value={minuteV} onChange={(e) => onMinute(e.target.value)}>
          {minutes.map((m) => (
            <option key={m} value={String(m).padStart(2, "0")}>{String(m).padStart(2, "0")}</option>
          ))}
        </select>
        <div className="bv-dt-ampm">
          <button
            type="button"
            className={`bv-dt-ampm-btn ${ampm === "AM" ? "on" : ""}`}
            onClick={() => onAmpm("AM")}
          >
            AM
          </button>
          <button
            type="button"
            className={`bv-dt-ampm-btn ${ampm === "PM" ? "on" : ""}`}
            onClick={() => onAmpm("PM")}
          >
            PM
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Image Analysis Section ────────────────────────────────────
function ImageAnalysisSection({ patientId }) {

  const token = localStorage.getItem("token");
  if (!token) throw new Error("auth token missing");

  const fileRef = useRef();
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [message,   setMessage]   = useState("");
  const [language,  setLanguage]  = useState("english");
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState(null);

  // Date & Time (12-hour, AM/PM)
  const [dateVal, setDateVal] = useState("");
  const [hour12,  setHour12]  = useState("12");
  const [minuteV, setMinuteV] = useState("00");
  const [ampm,    setAmpm]    = useState("AM");

  const buildCreatedAtDate = () => {
    if (!dateVal) return null;
    let h = parseInt(hour12, 10) % 12;
    if (ampm === "PM") h += 12;
    return new Date(`${dateVal}T${String(h).padStart(2, "0")}:${minuteV}:00`);
  };

  const pickFile = (f) => {
    if (!f || !f.type.startsWith("image/")) return;
    setFile(f);
    setResult(null);
    const r = new FileReader();
    r.onload = (e) => setPreview(e.target.result);
    r.readAsDataURL(f);
  };

  const clear = () => {
    setFile(null); setPreview(null);
    setMessage(""); setResult(null);
    setDateVal(""); setHour12("12"); setMinuteV("00"); setAmpm("AM");
    if (fileRef.current) fileRef.current.value = "";
  };

  const analyse = async () => {
    if (!file || !patientId) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("patient_owner_id", patientId);
      fd.append("language", language);
      if (message.trim()) fd.append("message", message.trim());

      const dObj = buildCreatedAtDate();
      if (dObj) fd.append("created_at", dObj.toISOString().slice(0, 19));

      fd.append("file", file);

      const res = await fetch(
        `${BASE_URL}/api/ai/nutrients/care-coordinator/image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        }
      );

      const text = await res.text();
      let json;
      try { json = JSON.parse(text); }
      catch { throw new Error(`HTTP ${res.status} · ${text.slice(0, 200)}`); }

      if (!res.ok) {
        throw new Error(json?.detail || json?.message || `HTTP ${res.status}`);
      }

      setResult({ ok: true, data: json });
    } catch (e) {
      setResult({ ok: false, data: e.message });
    } finally {
      setLoading(false);
    }
  };

  const mid = (lo, hi) => Math.round((lo + hi) / 2);

  function NutritionCard({ json }) {
    const ai    = json?.data?.ai_chat_response ?? {};
    const name  = ai.dish_name          ?? "Unknown dish";
    const conf  = ai.confidence_percent ?? null;
    const notes = ai.notes              ?? "";
    const id    = ai.calorie_id         ?? "";

    const portLo = ai.portion_g_range?.[0];
    const portHi = ai.portion_g_range?.[1];

    const metrics = [
      { label: "Calories", value: mid(ai.calories_kcal_range?.[0], ai.calories_kcal_range?.[1]), unit: "kcal", range: `${ai.calories_kcal_range?.[0]}–${ai.calories_kcal_range?.[1]}`,    color: "#7F77DD" },
      { label: "Protein",  value: mid(ai.protein_g_range?.[0],     ai.protein_g_range?.[1]),     unit: "g",    range: `${ai.protein_g_range?.[0]}–${ai.protein_g_range?.[1]} g`,         color: "#378ADD" },
      { label: "Fat",      value: mid(ai.fat_g_range?.[0],         ai.fat_g_range?.[1]),         unit: "g",    range: `${ai.fat_g_range?.[0]}–${ai.fat_g_range?.[1]} g`,                 color: "#EF9F27" },
      { label: "Carbs",    value: mid(ai.carbs_g_range?.[0],       ai.carbs_g_range?.[1]),       unit: "g",    range: `${ai.carbs_g_range?.[0]}–${ai.carbs_g_range?.[1]} g`,             color: "#639922" },
    ];

    const maxMacroG = Math.max(metrics[1].value, metrics[2].value, metrics[3].value);
    const macros = [
      { name: "Protein", value: metrics[1].value, dot: "#378ADD", fill: "#378ADD" },
      { name: "Fat",     value: metrics[2].value, dot: "#EF9F27", fill: "#EF9F27" },
      { name: "Carbs",   value: metrics[3].value, dot: "#639922", fill: "#639922" },
    ];

    return (
      <div className="bv-nc-card">
        <div className="bv-nc-header">
          <div className="bv-nc-icon" aria-hidden="true">🥗</div>
          <div className="bv-nc-header-text">
            <span className="bv-nc-name">{name}</span>
            {conf !== null && (
              <span className="bv-nc-conf-badge">✓ {conf}% confidence</span>
            )}
            <span className="bv-nc-meta">
              Nutrition analysis
              {portLo && portHi ? ` · ${portLo}–${portHi} g` : ""}
            </span>
          </div>
        </div>

        <div className="bv-nc-metrics">
          {metrics.map((m) => (
            <div key={m.label} className="bv-nc-metric">
              <p className="bv-nc-metric-label">{m.label}</p>
              <p className="bv-nc-metric-value" style={{ color: m.color }}>{m.value}</p>
              <p className="bv-nc-metric-unit">{m.unit}</p>
              <p className="bv-nc-metric-range">{m.range}</p>
            </div>
          ))}
        </div>

        <div className="bv-nc-bars">
          {macros.map((m) => (
            <div key={m.name} className="bv-nc-bar-row">
              <div className="bv-nc-bar-label">
                <span className="bv-nc-bar-dot" style={{ background: m.dot }} />
                <span className="bv-nc-bar-name">{m.name}</span>
              </div>
              <div className="bv-nc-bar-val">{m.value} g</div>
              <div className="bv-nc-bar-track">
                <div
                  className="bv-nc-bar-fill"
                  style={{ width: `${Math.round((m.value / maxMacroG) * 100)}%`, background: m.fill }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="bv-nc-divider" />

        {notes && (
          <div className="bv-nc-notes">
            <span className="bv-nc-notes-icon" aria-hidden="true">📋</span>
            <p className="bv-nc-notes-text">{notes}</p>
          </div>
        )}

        <div className="bv-nc-footer">
          {id && <span className="bv-nc-id">ID: {id}</span>}
          {portLo && portHi && (
            <span className="bv-nc-portion-pill">⚖ {portLo}–{portHi} g</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bv-section-divider">
        <span className="bv-section-divider-label">Nutrition Analysis</span>
      </div>

      <div className="bv-ia-card">
        {/* Drop zone */}
        <div
          className={`bv-ia-drop ${file ? "filled" : ""}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files[0]); }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => pickFile(e.target.files[0])}
          />
          {preview
            ? <img src={preview} alt="preview" className="bv-ia-preview" />
            : (
              <>
                <span className="bv-ia-drop-icon">🍽️</span>
                <span className="bv-ia-drop-label">Click or drag to upload a food image</span>
                <span className="bv-ia-drop-sub">PNG · JPG · WEBP · max 10 MB</span>
              </>
            )
          }
          {file && !preview && <span className="bv-ia-drop-label">{file.name}</span>}
        </div>

        {/* Language */}
        <div className="bv-ia-row">
          <span className="bv-ia-sublabel">Language</span>
          <select
            className="bv-ia-sel"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {["english", "hindi", "kannada", "tamil", "telugu"].map((l) => (
              <option key={l} value={l}>
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Date & Time — 12-hour with AM/PM */}
        <DateTimeField
          dateVal={dateVal}
          hour12={hour12}
          minuteV={minuteV}
          ampm={ampm}
          onDate={setDateVal}
          onHour={setHour12}
          onMinute={setMinuteV}
          onAmpm={setAmpm}
        />

        {/* Optional note */}
        <div className="bv-field" style={{ "--ac": "#8b5cf6", marginTop: 10 }}>
          <label className="bv-field-label">
            Note{" "}
            <em style={{ fontStyle: "normal", opacity: 0.5, fontSize: "11px", marginLeft: 4 }}>
              optional
            </em>
          </label>
          <textarea
            className="bv-ia-textarea"
            placeholder="Type a description for image..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
          />
        </div>

        {/* Actions */}
        <div className="bv-ia-actions">
          <button
            className="bv-btn-primary"
            onClick={analyse}
            disabled={!file || !patientId || loading}
          >
            {loading
              ? <><span className="bv-spinner" /> Analysing…</>
              : "🔍 Analyse & save"
            }
          </button>
          {file && (
            <button className="bv-btn-ghost" onClick={clear}>Clear</button>
          )}
        </div>

        {/* Result */}
        {result && (
          <div className="bv-ia-result">
            {result.ok ? (
              <NutritionCard json={result.data} />
            ) : (
              <span className="bv-smsg err">✕ {result.data}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function BaselineView({ summaryPatient, programData }) {
  const patientId = summaryPatient?.patient_id || summaryPatient?.owner_id;
  const baselines = programData?.medical_assets || [];
  const ncd       = programData?.ncd_data;
  const profile   = programData?.user_profile || summaryPatient?.user_profile;

  const [activeIdx, setActiveIdx] = useState(baselines.length > 0 ? baselines.length - 1 : -1);
  const [tab,       setTab]       = useState("view");
  const [isNew,     setIsNew]     = useState(baselines.length === 0);

  const emptyDraft = { ecg_link: "", heart_sound_link: "", lung_sound_link: "", bmi_link: "", labFile: null };
  const [draft,   setDraft]   = useState(emptyDraft);
  const [saving,  setSaving]  = useState(false);
  const [saveMsg, setSaveMsg] = useState({ text: "", ok: true });

  const activeBl    = baselines[activeIdx] || null;
  const noBaselines = baselines.length === 0;

  useEffect(() => {
    if (tab === "edit" && activeBl && !isNew) {
      setDraft({
        ecg_link:         activeBl.ecg_link         || "",
        heart_sound_link: activeBl.heart_sound_link || "",
        lung_sound_link:  activeBl.lung_sound_link  || "",
        bmi_link:         activeBl.bmi_link         || "",
        labFile:          null,
      });
    }
    if (isNew) setDraft(emptyDraft);
  }, [tab, activeIdx, isNew]); // eslint-disable-line

  const handleSelect = (idx) => { setActiveIdx(idx); setIsNew(false); setTab("view"); setSaveMsg({ text: "", ok: true }); };
  const handleNew    = ()    => { setActiveIdx(-1); setIsNew(true); setTab("edit"); setSaveMsg({ text: "", ok: true }); };
  const handleChange = (key, val) => setDraft((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg({ text: "", ok: true });
    try {
      const blNum = isNew
        ? generateBaselineNumber(baselines.length)
        : (activeBl?.baseline_number || generateBaselineNumber(baselines.length));

      await saveMedicalAssets({
        patientId,
        baselineNumber:  blNum,
        ecgLink:         draft.ecg_link         || null,
        heartSoundLink:  draft.heart_sound_link || null,
        lungSoundLink:   draft.lung_sound_link  || null,
        bmiLink:         draft.bmi_link         || null,
        labReport:       draft.labFile          || null,
      });
      setSaveMsg({ text: "Saved! Reloading…", ok: true });
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      setSaveMsg({ text: e.message || "Save failed. Try again.", ok: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bv-root">
      {/* ── SINGLE COLUMN: NCD panel on top, assets below ── */}
      <div className="bv-stack">

        {/* NCD + patient profile */}
        <NcdPanel ncd={ncd} profile={profile} />

        {/* Divider */}
        <div className="bv-section-divider">
          <span className="bv-section-divider-label">Medical Assets</span>
        </div>

        {/* Medical assets area */}
        <div className="bv-assets-area">

          {/* Top bar */}
          <div className="bv-topbar">
            <div className="bv-topbar-left">
              <span className="bv-title">Baselines</span>
              {!noBaselines && (
                <span className="bv-count">{baselines.length} baseline{baselines.length !== 1 ? "s" : ""}</span>
              )}
            </div>

            {/* View / Edit toggle */}
            {!noBaselines && activeIdx >= 0 && (
              <div className="bv-toggle">
                <button
                  className={`bv-toggle-btn ${tab === "view" ? "on" : ""}`}
                  onClick={() => { setTab("view"); setIsNew(false); }}
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <ellipse cx="6.5" cy="6.5" rx="5.5" ry="3.5" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="6.5" cy="6.5" r="1.6" fill="currentColor"/>
                  </svg>
                  View
                </button>
                <button
                  className={`bv-toggle-btn ${tab === "edit" ? "on" : ""}`}
                  onClick={() => { setTab("edit"); setIsNew(false); }}
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M9 1.5l2.5 2.5L4 11.5H1.5V9L9 1.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Baseline strip */}
          {!noBaselines && (
            <BaselineStrip
              baselines={baselines}
              activeIdx={activeIdx}
              onSelect={handleSelect}
              onNew={handleNew}
            />
          )}

          {/* Empty state */}
          {noBaselines && tab !== "edit" && (
            <div className="bv-empty">
              <div className="bv-empty-icon">🩺</div>
              <p className="bv-empty-head">No baselines recorded yet</p>
              <p className="bv-empty-sub">Create the first baseline to attach medical reports.</p>
              <button className="bv-btn-primary" onClick={handleNew}>+ Create BL-001</button>
            </div>
          )}

          {/* VIEW */}
          {tab === "view" && activeBl && (
            <div className="bv-panel">
              <div className="bv-panel-meta">
                <span className="bv-panel-num">{formatBaselineNumber(activeBl.baseline_number)}</span>
                <span className="bv-panel-date">{fmtDate(activeBl.created_at)}</span>
              </div>
              <div className="bv-arows">
                {SLOTS.map((s) => (
                  <AssetRow
                    key={s.key}
                    icon={s.icon}
                    label={s.label}
                    url={activeBl[s.reportKey] || null}
                    accent={s.accent}
                  />
                ))}
                <AssetRow
                  icon="📄"
                  label="Lab Report"
                  url={labReportUrl(activeBl.lab_report_key)}
                  accent="#8b5cf6"
                />
              </div>
            </div>
          )}

          {/* EDIT / CREATE */}
          {tab === "edit" && (
            <div className="bv-panel edit">
              <div className="bv-panel-meta">
                <span className="bv-panel-num">
                  {isNew
                    ? `New — ${generateBaselineNumber(baselines.length)}`
                    : `Editing — BL-${String(activeBl?.baseline_number || "").padStart(3, "0")}`}
                </span>
                <span className="bv-panel-date">All fields optional</span>
              </div>

              <div className="bv-fields">
                {SLOTS.map((s) => (
                  <LinkField
                    key={s.key}
                    icon={s.icon}
                    label={s.label}
                    hint={s.hint}
                    fieldKey={s.key}
                    value={draft[s.key]}
                    onChange={handleChange}
                    accent={s.accent}
                  />
                ))}
                <LabField
                  file={draft.labFile}
                  onChange={(f) => setDraft((p) => ({ ...p, labFile: f }))}
                />
              </div>

              <div className="bv-save-row">
                <button className="bv-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving
                    ? <><span className="bv-spinner" />Saving…</>
                    : <>💾 {isNew ? `Save ${generateBaselineNumber(baselines.length)}` : `Update BL-${String(activeBl?.baseline_number || "").padStart(3, "0")}`}</>
                  }
                </button>
                {!isNew && (
                  <button className="bv-btn-ghost" onClick={() => setTab("view")}>Cancel</button>
                )}
                {saveMsg.text && (
                  <span className={`bv-smsg ${saveMsg.ok ? "ok" : "err"}`}>
                    {saveMsg.ok ? "✓" : "✕"} {saveMsg.text}
                  </span>
                )}
              </div>
            </div>
          )}

        </div>{/* /bv-assets-area */}
       <ImageAnalysisSection patientId={patientId} />
      </div>{/* /bv-stack */}
    </div>
  );
}